import { useState } from 'react'
import {
  Checkbox,
  Combobox,
  Group,
  Loader,
  Pill,
  PillsInput,
  ScrollArea,
  UnstyledButton,
  useCombobox,
} from '@mantine/core'

export type CountComboboxOption = {
  value: string
  label: string
  disabled?: boolean
}

export type CountComboboxGroup = {
  label: string
  options: CountComboboxOption[]
}

type CountComboboxProps = {
  label: string
  options: CountComboboxOption[] | CountComboboxGroup[]
  selectedValues: string[]
  onSelectedValuesChange: (values: string[]) => void
  disabled?: boolean
  loading?: boolean
}

const isGroupedOptions = (
  options: CountComboboxOption[] | CountComboboxGroup[],
): options is CountComboboxGroup[] => options.some((option) => 'options' in option)

export const CountCombobox = ({
  label,
  options,
  selectedValues,
  onSelectedValuesChange,
  disabled = false,
  loading = false,
}: CountComboboxProps) => {
  const [search, setSearch] = useState('')
  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
  })
  const grouped = isGroupedOptions(options)
  const flatOptions = grouped ? options.flatMap((group) => group.options) : options
  const normalizedSearch = search.trim().toLocaleLowerCase()
  const filteredOptions = grouped
    ? options
        .map((group) => ({
          ...group,
          options: group.label.toLocaleLowerCase().includes(normalizedSearch)
            ? group.options
            : group.options.filter((option) =>
                option.label.toLocaleLowerCase().includes(normalizedSearch),
              ),
        }))
        .filter((group) => group.options.length > 0)
    : options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedSearch))
  const filteredFlatOptions = isGroupedOptions(filteredOptions)
    ? filteredOptions.flatMap((group) => group.options)
    : filteredOptions
  const allEnabledValues = flatOptions
    .filter((option) => !option.disabled)
    .map((option) => option.value)
  const labelLower = label.toLowerCase()

  const handleOptionSubmit = (value: string) => {
    if (flatOptions.find((option) => option.value === value)?.disabled) return

    const nextValues = selectedValues.includes(value)
      ? selectedValues.filter((selectedValue) => selectedValue !== value)
      : [...selectedValues, value]

    onSelectedValuesChange(nextValues)
  }

  const handleSelectAll = () => {
    onSelectedValuesChange(allEnabledValues)
  }

  const handleDeselectAll = () => {
    onSelectedValuesChange([])
  }

  /*
    `Checkbox.Indicator`, not `Checkbox`: a real checkbox renders an `<input>` inside a
    `<label>`, and a tap on the label makes the browser dispatch a second, synthesised click
    on the input. Both bubble to `Combobox.Option`, so one tap called `onOptionSubmit` twice
    and the toggle cancelled itself out. Desktop was unaffected only because Mantine's option
    calls `preventDefault()` on `mousedown`, which suppresses the label forwarding for mouse
    input — touch input has no such mousedown to cancel. The indicator is a plain div with no
    input and no label, so it cannot forward anything. `aria-selected` carries the checked
    state that the removed input used to expose, matching Mantine's own MultiSelect options.
  */
  const renderOption = (option: CountComboboxOption) => (
    <Combobox.Option
      value={option.value}
      key={option.value}
      disabled={option.disabled}
      aria-selected={selectedValues.includes(option.value)}
    >
      <Group
        gap={'xs'}
        wrap={'nowrap'}
      >
        <Checkbox.Indicator
          checked={selectedValues.includes(option.value)}
          disabled={option.disabled}
        />
        {option.label}
      </Group>
    </Combobox.Option>
  )

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      withinPortal
      position={'bottom-start'}
    >
      {/*
        DropdownTarget, not Target: it anchors the dropdown and nothing else. Combobox.Target
        would also claim `store.targetRef` and attach keyboard/aria handling to this div, which
        the EventsTarget below already owns on a focusable element. Matches Mantine's own
        MultiSelect composition.
      */}
      <Combobox.DropdownTarget>
        <PillsInput
          pointer
          disabled={disabled}
          onClick={() => !disabled && combobox.toggleDropdown()}
          rightSection={loading ? <Loader size={'xs'} /> : <Combobox.Chevron />}
        >
          <Pill.Group>
            <Pill>
              {label} ({selectedValues.length} / {flatOptions.length})
            </Pill>
            <Combobox.EventsTarget
              targetType={'button'}
              withExpandedAttribute
            >
              <PillsInput.Field
                type={'hidden'}
                disabled={disabled}
                aria-label={label}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={`Search ${labelLower}`}
          aria-label={`Search ${labelLower}`}
        />
        <Group
          justify={'space-between'}
          px={'sm'}
          py={4}
        >
          <UnstyledButton
            fz={'sm'}
            c={'blue'}
            onClick={selectedValues.length > 0 ? handleDeselectAll : handleSelectAll}
          >
            {selectedValues.length > 0 ? `deselect all ${labelLower}` : `select all ${labelLower}`}
          </UnstyledButton>
        </Group>
        <Combobox.Options>
          <ScrollArea.Autosize mah={300} type={'auto'}>
            {filteredFlatOptions.length === 0 ? (
              <Combobox.Empty>No {labelLower} found</Combobox.Empty>
            ) : isGroupedOptions(filteredOptions) ? (
              filteredOptions.map((group) => (
                <Combobox.Group key={group.label} label={group.label}>
                  {group.options.map(renderOption)}
                </Combobox.Group>
              ))
            ) : (
              filteredOptions.map(renderOption)
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
